"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, FileSpreadsheet, FileText } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { exportToExcel, exportToPDF } from "@/lib/excelExport";

interface OperationsDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    staffWorkload: {
      chartData: Array<{
        date: string;
        [staffName: string]: string | number;
      }>;
      topStaff: string[];
      imbalance: boolean;
      imbalanceDetails: Array<{
        name: string;
        appointments: number;
      }> | null;
    };
    dailyOperations: Array<{
      date: string;
      dateStr: string;
      scheduled: number;
      completed: number;
      cancelled: number;
      total: number;
      completionRate: number;
    }>;
  };
}

export function OperationsDashboard({ data }: OperationsDashboardProps) {

  const COLORS = [
    "#0ea5e9",
    "#8b5cf6",
    "#ec4899",
    "#f59e0b",
    "#10b981",
    "#ef4444",
    "#6366f1",
    "#14b8a6",
  ];

  const getExportData = () => {
    return data.dailyOperations.map((day) => ({
      Date: day.dateStr,
      Scheduled: day.scheduled,
      Completed: day.completed,
      Cancelled: day.cancelled,
      Total: day.total,
      "Completion Rate (%)": day.completionRate,
    }));
  };

  const handleExportExcel = () => {
    exportToExcel(getExportData(), "Operations_Report", "Daily Operations");
  };

  const handleExportPDF = () => {
    exportToPDF(getExportData(), "Operations_Report", {
      title: "Operations Report",
      subtitle: "Daily operations summary",
      dateRange: data.dateRange,
    });
  };

  return (
    <div className="space-y-4">
      {/* Staff Workload Comparison Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Staff Workload Comparison</CardTitle>
            {data.staffWorkload.imbalance && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" />
                Workload Imbalance
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {data.staffWorkload.imbalance && data.staffWorkload.imbalanceDetails && (
            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-md">
              <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200">
                <AlertTriangle className="h-4 w-4" />
                <span>
                  <strong>{data.staffWorkload.imbalanceDetails[0]?.name}</strong>{" "}
                  has {data.staffWorkload.imbalanceDetails[0]?.appointments}{" "}
                  appointments vs{" "}
                  <strong>{data.staffWorkload.imbalanceDetails[1]?.name}</strong>{" "}
                  with {data.staffWorkload.imbalanceDetails[1]?.appointments}.
                  Consider redistributing workload.
                </span>
              </div>
            </div>
          )}
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data.staffWorkload.chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              {data.staffWorkload.topStaff.map((name, idx) => (
                <Bar
                  key={name}
                  dataKey={name}
                  fill={COLORS[idx % COLORS.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Daily Operations Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Daily Operations Summary</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportExcel}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Scheduled</TableHead>
                  <TableHead className="text-right">Completed</TableHead>
                  <TableHead className="text-right">Cancelled</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Completion Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.dailyOperations
                  .slice()
                  .reverse()
                  .map((day, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">
                        {day.dateStr}
                      </TableCell>
                      <TableCell className="text-right">
                        {day.scheduled}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        {day.completed}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {day.cancelled}
                      </TableCell>
                      <TableCell className="text-right">{day.total}</TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant={
                            day.completionRate >= 80
                              ? "default"
                              : day.completionRate >= 60
                              ? "secondary"
                              : "destructive"
                          }
                        >
                          {day.completionRate}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

